import { useCallback, useRef, type ReactNode } from 'react';

import { RAIL_MIN_WIDTH, RAIL_STUB_WIDTH } from '../model/rails.js';

/**
 * The panel / rail frame: header, collapse stub, drag resize (design 166–180,
 * 1144–1160).
 *
 * Both rails are this component with different contents, which is the ledger's
 * first row and its "expected lift: immediately". Phases 3–8 put panels inside
 * these rails rather than beside them, so nothing else needs to grow a frame.
 *
 * ## The stub is the same element, not a different one
 *
 * A collapsed rail is 30 px with a vertical label and a reopen button. Rendering
 * it as a separate branch of the parent is how a rail ends up in two states that
 * drift; here the frame owns both, so the parent hands it contents and never
 * asks whether they will be seen.
 *
 * ## Resize
 *
 * The design gives the *right* rail a drag handle and derives the left rail's
 * width from the viewport. Both are draggable here, because Phase 2's own manual
 * check asks for "resize both rails to each end and reload" and because a fixed
 * left rail at 1280 is the width the roster is least able to spare. Same
 * component, same clamp, `max` supplied by `railLayout` — which recomputes it
 * against the live viewport on every render, so a rail dragged wide while its
 * neighbour was collapsed gives the width back when the neighbour reopens.
 */

export interface RailFrameProps {
  readonly side: 'left' | 'right';
  readonly label: string;
  readonly open: boolean;
  readonly width: number;
  readonly maxWidth: number;
  readonly onToggle: () => void;
  readonly onResize: (width: number) => void;
  /** Right of the title in the open header — the design's FOCUS button lives here. */
  readonly headerExtra?: ReactNode;
  readonly children: ReactNode;
}

export function RailFrame(props: RailFrameProps): JSX.Element {
  const testid = 'rail-' + props.side;

  if (!props.open) {
    return (
      <div
        data-testid={testid}
        data-rail-state="closed"
        style={{
          flex: 'none',
          width: RAIL_STUB_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          paddingTop: 6,
          background: 'var(--surface)',
        }}
      >
        <button
          type="button"
          data-testid={testid + '-toggle'}
          onClick={props.onToggle}
          aria-label={'SHOW ' + props.label}
          style={STUB_BUTTON}
        >
          {props.side === 'left' ? '›' : '‹'}
        </button>
        <span
          style={{
            writingMode: 'vertical-rl',
            fontFamily: 'var(--font-data)',
            fontSize: 11,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          {props.label}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      data-rail-state="open"
      data-rail-width={props.width}
      style={{
        position: 'relative',
        flex: 'none',
        width: props.width,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--surface)',
      }}
    >
      <ResizeEdge
        side={props.side}
        width={props.width}
        maxWidth={props.maxWidth}
        onResize={props.onResize}
        label={props.label}
      />

      <div
        data-mat="chrome hdr"
        style={{
          flex: 'none',
          height: 33,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: props.side === 'left' ? '0 8px 0 14px' : '0 8px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {/*
          `h2` under the identity row's `h1`. Every rail header was a `<span>`, so
          the console's panels were invisible to a screen reader's outline even
          though they are the visible structure of the page. The element changes;
          nothing about the type does.
        */}
        <h2
          data-testid={testid + '-label'}
          style={{
            flex: 1,
            minWidth: 0,
            margin: 0,
            fontFamily: 'var(--font-data)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--text)',
          }}
        >
          {props.label}
        </h2>
        {props.headerExtra}
        <button
          type="button"
          data-testid={testid + '-toggle'}
          onClick={props.onToggle}
          aria-label={'HIDE ' + props.label}
          style={STUB_BUTTON}
        >
          {props.side === 'left' ? '‹' : '›'}
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {props.children}
      </div>
    </div>
  );
}

/**
 * The drag strip.
 *
 * Pointer capture rather than window listeners so a drag that leaves the strip —
 * every drag — keeps delivering moves, and so a pointer released outside the
 * window still ends it. The arrow keys move it too: a 6 px target is not
 * something everyone can hit, and the rail is the console's one adjustable
 * dimension.
 */
function ResizeEdge(props: {
  readonly side: 'left' | 'right';
  readonly width: number;
  readonly maxWidth: number;
  readonly onResize: (width: number) => void;
  readonly label: string;
}): JSX.Element {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);
  const { side, width, maxWidth, onResize } = props;

  const clamp = useCallback(
    (value: number) => Math.max(RAIL_MIN_WIDTH, Math.min(maxWidth, Math.round(value))),
    [maxWidth],
  );

  // Nothing writes the clamp back into the store. `railLayout` already hands
  // this component the clamped width, so what is drawn is always legal; the
  // stored value stays whatever the operator asked for, and a window dragged
  // narrow and back finds its rails where it left them.

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    event.preventDefault();
    drag.current = { startX: event.clientX, startWidth: width };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const start = drag.current;
    if (start === null) return;
    const delta = event.clientX - start.startX;
    onResize(clamp(start.startWidth + (side === 'left' ? delta : -delta)));
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const step = event.shiftKey ? 40 : 8;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onResize(clamp(width + (side === 'left' ? -step : step)));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onResize(clamp(width + (side === 'left' ? step : -step)));
    }
  };

  return (
    <div
      data-testid={'rail-' + side + '-resize'}
      role="separator"
      aria-orientation="vertical"
      aria-label={'RESIZE ' + props.label}
      aria-valuenow={width}
      aria-valuemin={RAIL_MIN_WIDTH}
      aria-valuemax={maxWidth}
      /*
       * `maxWidth` is computed live from the Centre-Pane-Keeps-640 rule, so with the
       * other rail wide it can equal the minimum — and a separator advertising
       * valuemin === valuemax === valuenow is telling a screen reader it cannot be
       * adjusted while still being operable by everyone else. Say so properly: the
       * handle is disabled for as long as the arithmetic leaves it nowhere to go.
       */
      aria-disabled={maxWidth <= RAIL_MIN_WIDTH ? true : undefined}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        [side === 'left' ? 'right' : 'left']: -3,
        zIndex: 'var(--z-rail-edge)',
        width: 6,
        cursor: 'col-resize',
      }}
    />
  );
}

const STUB_BUTTON = {
  flex: 'none',
  width: 22,
  height: 22,
  padding: 0,
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text-3)',
  fontFamily: 'var(--font-data)',
  fontSize: 11,
  lineHeight: 1,
  cursor: 'pointer',
} as const;
